import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { GiftIcon } from '@heroicons/react/24/outline';
import { useDiffitUserContext } from '../../contexts/DiffitUserContext';
import { Mixpanel } from '../../services/Mixpanel';
import { getUnseenReferralRewards, markReferralRewardsSeen } from '../../services/ReferralService';
import TemplateRewardLink from './TemplateRewardLink';
import { getRefereeRewardCopy } from '../../utils/rewardCopy';

/**
 * Tells a referee a colleague gifted them something.
 *
 * A referee's reward is granted during their own signup or first generate — requests whose
 * responses are a user object and a packet, with nothing on screen to say a reward happened.
 * Without this they are simply never told.
 *
 * Server-tracked rather than localStorage: `seen_at` lives on the grant, so the modal shows
 * once per reward across devices, and a referee who earns again later (a signed_up program
 * and a generated one can both pay) is told the second time too.
 */
// Module-level, so it survives remounts: this component is conditionally *mounted* (on the
// wizard being closed, and off the pricing page), and anything that flips those back and forth
// would otherwise refire the GET and log a second "Shown" against the same rewards.
const fetchedForUsers = new Set();
const recheckSubscribers = new Set();

// Reward types whose grant moves user_info.trial_end_date, and so need the user object
// refreshed before we can name the date it now points at.
const DATE_BEARING_REWARDS = new Set(['trial_days', 'promo_code']);

/**
 * Ask this modal to look again, because a reward may have been granted since it last did.
 *
 * The fetch below runs the moment `diffitUser.id` lands and then latches. That is right for
 * every path that grants during sign-up or first generate — the grant is already written when
 * the user object arrives. It is wrong for Base's referral drain, which POSTs the stored
 * referral code *after* the user context is populated: the fetch has already come back empty
 * and latched by the time the grant exists, so without this the referee is told nothing until
 * their next full page load.
 *
 * Clearing the latch is the half that matters when the modal isn't mounted (the wizard is
 * open, or we're on the pricing page) — it will fetch when it mounts. The subscriber ping is
 * for when it is mounted and needs to re-run an effect it has already run.
 */
export function recheckRefereeRewards(userId) {
  if (userId) fetchedForUsers.delete(userId);
  recheckSubscribers.forEach((notify) => notify());
}

export default function RefereeRewardModal() {
  const { isSignedIn, diffitUser, hasActiveSubscription, refreshDiffitUser } =
    useDiffitUserContext();
  const [rewards, setRewards] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [recheckCount, setRecheckCount] = useState(0);

  // Held in a ref, not a dep: Base rebuilds refreshDiffitUser every render, so depending on
  // it would re-run the fetch effect on every parent render (the latch would swallow the
  // work, but the churn is pointless).
  const refreshRef = useRef(refreshDiffitUser);
  useEffect(() => {
    refreshRef.current = refreshDiffitUser;
  });

  useEffect(() => {
    const bump = () => setRecheckCount((count) => count + 1);
    recheckSubscribers.add(bump);
    return () => {
      recheckSubscribers.delete(bump);
    };
  }, []);

  useEffect(() => {
    // Keyed on the user id, not `[]`: signing in mid-session mounts nothing new, and a
    // freshly signed-up referee is exactly the case this modal exists for. `recheckCount`
    // is what lets recheckRefereeRewards re-run this after the latch is cleared.
    if (!isSignedIn || !diffitUser?.id) return;
    if (fetchedForUsers.has(diffitUser.id)) return;
    fetchedForUsers.add(diffitUser.id);
    let cancelled = false;

    getUnseenReferralRewards()
      .then(async (response) => {
        const unseen = response?.data?.rewards || [];
        if (cancelled || unseen.length === 0) return;

        // Refresh *before* opening, not after: the grant moved trial_end_date, and on Base's
        // referral drain path it did so after the GET /user that filled this context. Opening
        // first would flash a date one reward short of the truth. Awaited so the modal's first
        // paint already has the post-grant date; a failure just leaves the day-count wording.
        if (unseen.some((reward) => DATE_BEARING_REWARDS.has(reward.reward_type))) {
          await refreshRef.current?.();
          if (cancelled) return;
        }

        setRewards(unseen);
        setIsOpen(true);
        Mixpanel.track('Referee Reward Modal Shown', {
          reward_count: unseen.length,
          reward_types: unseen.map((r) => r.reward_type),
        });
      })
      // Silent by contract (see ReferralService): a 404 means the flag is off, and nobody
      // asked for this — an error dialog would be worse than saying nothing.
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [isSignedIn, diffitUser?.id, recheckCount]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    Mixpanel.track('Referee Reward Modal Dismissed', { reward_count: rewards.length });
    // Fire-and-forget: a failure here just means it shows again next load, which is a much
    // better failure than blocking the close.
    markReferralRewardsSeen(rewards.map((reward) => reward.id)).catch(() => {});
  }, [rewards]);

  if (rewards.length === 0) return null;

  // Withheld from an already-paid teacher: _async_apply_reward skips the trial_end_date
  // update when user.is_paid, so their date is untouched by this grant and naming it would
  // dress up their existing subscription as the reward. They get the day-count wording.
  const trialEndDateIso = hasActiveSubscription ? null : diffitUser?.trial_end_date_iso || null;
  const copy = getRefereeRewardCopy(rewards, { trialEndDateIso });
  // Nothing describable came back (an unknown reward_type, or an empty reward_json). Better
  // to stay shut than to open on a title with nothing under it.
  if (!copy) return null;

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as='div' className='relative z-50' onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'>
          <div className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
        </Transition.Child>

        <div className='fixed inset-0 z-50 w-screen overflow-y-auto'>
          <div className='flex min-h-full items-center justify-center p-4'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 translate-y-4'
              enterTo='opacity-100 translate-y-0'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 translate-y-0'
              leaveTo='opacity-0 translate-y-4'>
              <Dialog.Panel className='relative w-full transform rounded-2xl bg-white px-8 py-8 text-left shadow-xl transition-all sm:my-8 sm:max-w-md'>
                <div className='flex justify-center'>
                  <div className='flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100'>
                    <GiftIcon className='h-6 w-6 text-emerald-600' />
                  </div>
                </div>
                <Dialog.Title as='h2' className='mt-4 text-center text-2xl font-bold text-gray-800'>
                  <span>{copy.title}</span>
                </Dialog.Title>
                {/* The title names the benefit; this completes it and says where it came
                    from. The attribution earns its place — a teacher who followed a
                    forwarded invite or a social post has no other way to know. */}
                <p className='mt-2 text-center text-sm text-gray-500'>
                  <span>{copy.subtext}</span>
                </p>
                {rewards
                  .filter((reward) => reward.reward_type === 'template')
                  .map((reward) => (
                    <TemplateRewardLink key={reward.id} reward={reward} />
                  ))}

                <button
                  type='button'
                  onClick={handleClose}
                  className='mt-6 w-full rounded-md bg-emerald-600 px-3 py-2 font-semibold text-white hover:bg-emerald-500'>
                  <span>Got it</span>
                </button>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
